import _ from 'lodash';

import { PendingItemType } from '@appTypes/enums';
import type {
  AdvancementHistoryItem,
  AdvancementHistoryRequest,
} from '@appTypes/esb';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';

type BodyNoAdvancementType = Omit<
  AdvancementHistoryRequest['body'],
  'advancementType'
>;

interface BulkRequest extends Omit<AdvancementHistoryRequest, 'body'> {
  body: BodyNoAdvancementType;
}

export const purchaseOrderApi = esbApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * @esbEndpoint POST /advancements/advancementHistory
     *
     * This is used only for Step#1 Purchase Order
     */
    getBulkAdvancementHistoryToPurchase: builder.query<
      AdvancementHistoryItem[],
      {
        advancementTypes: PendingItemType[];
        payload: BulkRequest;
      }
    >({
      queryFn: async (arg, _query, _options, baseQuery) => {
        const url = '/advancements/advancementHistory';
        const params = arg.payload.params;

        const requests = arg.advancementTypes.map(type => {
          const body: AdvancementHistoryRequest['body'] = {
            ...arg.payload.body,
            advancementType: type,
          };

          return baseQuery({
            url,
            method: 'POST',
            params,
            body,
          });
        });

        const items = (await Promise.all(requests)).flatMap(response => {
          const result = (response.data || []) as AdvancementHistoryItem[];
          return result;
        });

        return { error: undefined, data: items };
      },
      providesTags: (result, error, arg) => {
        if (error || !result) return [];

        const statuses = _.chain(arg.payload.body.status)
          .uniq()
          .sort()
          .join(',')
          .value();

        return [
          tagFactory.AdvancementHistory.unitHistoryWithStatus(
            arg.payload.body.organizationGuid,
            statuses,
          ),
        ];
      },
    }),
  }),
});

export const {
  useGetBulkAdvancementHistoryToPurchaseQuery,
  useLazyGetBulkAdvancementHistoryToPurchaseQuery,
} = purchaseOrderApi;
